<template>
  <el-dialog
    title="新增故障单"
    :visible="visible"
    width="1000px"
    :close-on-click-modal="false"
    append-to-body
    @close="$emit('update:visible', false)"
  >
    <el-steps :active="deviceSource === 'external' && step === 2 ? 1 : step" align-center class="create-steps">
      <el-step title="选择设备类型" />
      <el-step v-if="deviceSource !== 'external'" title="选择故障设备" />
      <el-step title="填写故障信息" />
    </el-steps>
    <div v-if="step === 0" class="source-selection">
      <div class="source-title">请选择故障设备类型</div>
      <div class="source-description">设备是否已录入系统设备台账？</div>
      <div class="source-choices">
        <button type="button" class="source-choice" @click="changeDeviceSource('system')">
          <span class="source-choice-title">系统设备</span>
          <span>从设备台账中选择故障设备</span>
          <span class="source-choice-action">选择设备 →</span>
        </button>
        <button type="button" class="source-choice" @click="changeDeviceSource('external')">
          <span class="source-choice-title">非系统设备</span>
          <span>设备未录入台账，手工填写名称及位置</span>
          <span class="source-choice-action">填写故障信息 →</span>
        </button>
      </div>
    </div>
    <!-- 步骤一：筛选并选择设备 -->
    <div v-if="step === 1">
      <div class="step-title">第二步：选择故障设备</div>
      <div class="device-search-bar">
        <el-input
          v-model="deviceQuery.keyword"
          placeholder="设备名称/编号"
          clearable
          style="width: 160px"
          @keyup.enter.native="searchDevices"
        />
        <el-select v-model="deviceQuery.systemId" placeholder="消防系统" clearable style="width: 130px" @change="handleFormSystemChange">
          <el-option v-for="s in systemOptions" :key="s.uuid" :label="s.systemName" :value="s.uuid" />
        </el-select>
        <el-select v-model="deviceQuery.deviceTypeId" placeholder="设施类型" clearable style="width: 130px" @change="searchDevices">
          <el-option v-for="t in formTypeList" :key="t.uuid" :label="t.typeName" :value="t.uuid" />
        </el-select>
        <!-- 所属区域级联选择 -->
        <AreaCascader
          v-model="deviceQuery.areaId"
          :options="areaTree"
          placeholder="所属区域"
          clearable
          style="width: 160px"
          @change="searchDevices"
        />
        <el-button type="primary" size="small" icon="el-icon-search" @click="searchDevices">搜索</el-button>
        <el-button size="small" icon="el-icon-refresh" @click="handleReset">重置</el-button>
      </div>
      <div class="device-select-tip">点击设备行，进入故障信息填写</div>
      <el-table
        class="device-select-table"
        v-loading="deviceLoading"
        :data="deviceList"
        border
        highlight-current-row
        style="width: 100%; margin-top: 12px"
        @current-change="handleSelectDevice"
      >
        <el-table-column prop="deviceCode" label="设备编号" width="180" align="center" />
        <el-table-column prop="deviceName" label="设备名称" width="150" align="center" />
        <el-table-column label="设备级别" width="85" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="deviceLevelTagType(row.deviceLevel)" size="mini">{{ deviceLevelLabel(row.deviceLevel) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="systemName" label="消防系统" width="120" align="center" />
        <el-table-column prop="areaPath" label="设备所属区域" show-overflow-tooltip />
      </el-table>
      <div class="pagination-wrap">
        <el-pagination
          :current-page="deviceQuery.pageNo"
          :page-size="deviceQuery.pageSize"
          :total="deviceTotal"
          layout="total, prev, pager, next"
          small
          @current-change="(p) => { deviceQuery.pageNo = p; searchDevices(); }"
        />
      </div>
    </div>

    <!-- 步骤二：填写故障信息 -->
    <div v-if="step === 2">
      <div class="step-title">
        {{ deviceSource === 'system' ? '第三步：填写故障信息' : '第二步：填写非系统设备故障信息' }}
      </div>

      <!-- 已选设备展示 -->
      <div v-if="deviceSource === 'system' && selectedDevice" class="selected-device-card">
        <span class="label">已选设备：</span>
        <span class="device-name">{{ selectedDevice.deviceName }}</span>
        <el-tag :type="deviceLevelTagType(selectedDevice.deviceLevel)" size="mini" style="margin-left: 8px">
          {{ deviceLevelLabel(selectedDevice.deviceLevel) }}
        </el-tag>
        <span class="device-area">{{ selectedDevice.areaPath }}</span>
      </div>

      <el-form ref="faultForm" :model="faultForm" :rules="faultRules" label-width="100px">
        <template v-if="deviceSource === 'external'">
          <el-form-item label="设备名称" prop="deviceName" :rules="[{ required: true, whitespace: true, message: '请输入设备名称', trigger: 'blur' }]">
            <el-input v-model="faultForm.deviceName" maxlength="100" placeholder="请输入非系统设备名称" />
          </el-form-item>
          <el-form-item label="所属区域" prop="areaId" :rules="[{ required: true, message: '请选择所属区域', trigger: 'change' }]">
            <AreaCascader v-model="faultForm.areaId" :options="areaTree" clearable style="width: 100%" />
          </el-form-item>
          <el-form-item label="具体位置" prop="installLocation" :rules="[{ required: true, whitespace: true, message: '请输入设备具体位置', trigger: 'blur' }]">
            <el-input v-model="faultForm.installLocation" maxlength="500" placeholder="请输入设备具体位置" />
          </el-form-item>
        </template>
        <el-form-item label="故障级别" prop="faultLevel">
          <el-radio-group v-model="faultForm.faultLevel">
            <el-radio label="normal">一般</el-radio>
            <el-radio label="urgent">紧急</el-radio>
          </el-radio-group>
          <div v-if="deviceSource === 'system'" class="level-tip">已根据设备级别设置默认值，可手动调整</div>
        </el-form-item>
        <el-form-item label="发现时间" prop="foundTime">
          <el-date-picker
            v-model="faultForm.foundTime"
            type="datetime"
            placeholder="默认取当前时间"
            style="width: 100%"
            value-format="yyyy-MM-dd HH:mm:ss"
          />
        </el-form-item>
        <el-form-item label="故障描述" prop="faultDesc">
          <el-input
            v-model="faultForm.faultDesc"
            type="textarea"
            :rows="3"
            placeholder="请描述故障现象"
          />
        </el-form-item>

        <el-form-item label="现场图片">
          <ImageUploader
            ref="faultImagesUploader"
            v-model="faultImageFileIds"
            size="md"
            :show-zoom="true"
          />
        </el-form-item>

        <!-- 分配方式 + 指定个人/小组 -->
        <AssignTypeFields
          :model="faultForm"
          team-type="fault"
          preset="fault"
          :active="visible"
          user-form-prop="assignUserIds"
          team-form-prop="assignTeamId"
          @navigate-to-team="handleNavigateToTeam"
          @navigate-to-org-user="handleNavigateToOrgUser"
        />
      </el-form>
    </div>

    <div slot="footer">
      <el-button @click="$emit('update:visible', false)">取消</el-button>
      <el-button v-if="step > 0" type="primary" plain class="step-prev-button" :disabled="submitting" @click="previousStep">上一步</el-button>
      <el-button v-if="step === 2" type="primary" :loading="submitting" @click="handleSubmit">提交</el-button>
    </div>
  </el-dialog>
</template>

<script>
// 服务
import { faultService } from './fault.service';
import { areaService } from '@/features/gnmk/area/area.service';
import { deviceTypeService } from '@/features/gnmk/deviceType/deviceType.service';
import { deviceService } from '@/features/gnmk/device/device.service';
// 组件
import AssignTypeFields from '@/features/gnmk/components/AssignTypeFields.vue';
import AreaCascader from '@/features/gnmk/components/AreaCascader.vue';

export default {
  name: 'FaultCreateDialog',
  components: {
    AssignTypeFields,
    AreaCascader
  },
  props: {
    visible: { type: Boolean, default: false }
  },
  data () {
    return {
      step: 0,
      deviceSource: '',
      // 设备搜索
      deviceLoading: false,
      deviceList: [],
      deviceTotal: 0,
      deviceQuery: { keyword: '', systemId: '', deviceTypeId: '', areaId: '', pageNo: 1, pageSize: 8 },
      systemOptions: [],
      // 全量设施类型列表（与 deviceDialog 命名一致）
      typeList: [],
      // 按当前选中消防系统过滤后的设施类型列表
      formTypeList: [],
      // 区域树
      areaTree: [],
      selectedDevice: null,
      faultImageFileIds: [],
      // 故障表单
      faultForm: {
        deviceName: '',
        areaId: '',
        installLocation: '',
        faultLevel: 'normal',
        foundTime: null,
        faultDesc: '',
        assignType: 'none',
        assignUserIds: [],
        assignTeamId: null
      },
      faultRules: {
        faultDesc: [{ required: true, message: '请填写故障描述', trigger: 'blur' }],
        faultLevel: [{ required: true, message: '请选择故障级别', trigger: 'change' }]
      },
      submitting: false
    };
  },
  created () {
    this.loadFilterOptions();
  },
  methods: {
    /** 与设备模块一致：A 重要 / B 一般 / C 普通 */
    deviceLevelLabel (level) {
      const map = { A: '重要设备', B: '一般设备', C: '普通设备' };
      const key = String(level || 'C').toUpperCase();
      return map[key] || map.C;
    },
    /** Element 标签色：红 / 橙 / 绿 */
    deviceLevelTagType (level) {
      const key = String(level || 'C').toUpperCase();
      if (key === 'A') return 'danger';
      if (key === 'B') return 'warning';
      return 'info';
    },

    /** A 级设备建单默认为紧急故障，其余为一般故障（与后端 FaultLevel 一致） */
    faultLevelCodeFromDevice (deviceLevel) {
      return String(deviceLevel || '').toUpperCase() === 'A' ? 'urgent' : 'normal';
    },

    loadFilterOptions () {
      // 加载消防系统列表
      deviceTypeService.listFireSystems().then(res => {
        this.systemOptions = res.data || [];
      }).catch(() => {});
      // 加载全量设施类型列表，切换系统时本地过滤（与 deviceDialog 逻辑一致）
      deviceTypeService.listDeviceTypes().then(res => {
        this.typeList = res.data || [];
      }).catch(() => {});
      // 加载区域树
      areaService.getAreaTree().then(res => {
        this.areaTree = res.data || [];
      }).catch(() => {});
    },

    /** 切换消防系统：清空设施类型选择，重新过滤类型列表，然后刷新设备 */
    handleFormSystemChange () {
      this.deviceQuery.deviceTypeId = '';
      this.formTypeList = this.deviceQuery.systemId
        ? this.typeList.filter(t => t.systemId === this.deviceQuery.systemId)
        : [];
      this.searchDevices();
    },

    /** 重置搜索条件 */
    handleReset () {
      this.deviceQuery = { keyword: '', systemId: '', deviceTypeId: '', areaId: '', pageNo: 1, pageSize: 8 };
      this.formTypeList = [];
      this.searchDevices();
    },

    /** 前往人员小组管理（关闭弹窗避免遮罩遮挡） */
    handleNavigateToTeam () {
      this.$emit('update:visible', false);
      this.$router.push({ name: 'teamList' }).catch(() => {});
    },
    /** 前往组织人员管理 */
    handleNavigateToOrgUser () {
      this.$emit('update:visible', false);
      this.$router.push({ name: 'orgUserList' }).catch(() => {});
    },

    searchDevices () {
      this.deviceLoading = true;
      deviceService.queryDevicePage({ ...this.deviceQuery, deviceStatus: null }).then(res => {
        const data = res.data;
        this.deviceList = data.list || data.rows || [];
        this.deviceTotal = data.total || 0;
      }).finally(() => {
        this.deviceLoading = false;
      });
    },

    changeDeviceSource (value) {
      if (value !== this.deviceSource) {
        this.faultForm.faultLevel = 'normal';
        this.selectedDevice = null;
      }
      this.deviceSource = value;
      if (value === 'system') this.searchDevices();
      this.step = value === 'external' ? 2 : 1;
      this.$nextTick(() => {
        if (this.$refs.faultForm) this.$refs.faultForm.clearValidate();
      });
    },

    previousStep () {
      if (this.submitting || (this.$refs.faultImagesUploader && this.$refs.faultImagesUploader.isUploading())) return;
      // 返回只改变当前步骤，保留设备、故障级别和已填写的表单。
      this.step = this.deviceSource === 'system' && this.step === 2 ? 1 : 0;
    },

    handleSelectDevice (device) {
      if (!device) return;
      // 返回后再次选择同一设备时，保留用户手工调整的故障级别。
      if (!this.selectedDevice || this.selectedDevice.uuid !== device.uuid) {
        this.faultForm.faultLevel = this.faultLevelCodeFromDevice(device.deviceLevel);
      }
      this.selectedDevice = device;
      this.step = 2;
    },

    handleSubmit () {
      if (this.submitting || this.step !== 2 || !['system', 'external'].includes(this.deviceSource)) return;
      if (this.deviceSource === 'system' && !this.selectedDevice) {
        this.$message.warning('请选择故障设备');
        return;
      }
      this.$refs.faultForm.validate(valid => {
        if (!valid) return;
        if (this.$refs.faultImagesUploader && this.$refs.faultImagesUploader.isUploading()) {
          this.$message.warning('图片上传中，请稍候再提交');
          return;
        }
        this.submitting = true;
        const doneImages = this.faultImageFileIds || [];
        const payload = {
          deviceSource: this.deviceSource,
          ...(this.deviceSource === 'system'
            ? { deviceId: this.selectedDevice.uuid }
            : {
              deviceName: this.faultForm.deviceName.trim(),
              areaId: this.faultForm.areaId,
              installLocation: this.faultForm.installLocation.trim()
            }),
          // 录入来源固定为人工录入（管理端新建均为此来源）
          faultSource: 'manual',
          faultLevel: this.faultForm.faultLevel,
          foundTime: this.faultForm.foundTime,
          faultDesc: this.faultForm.faultDesc,
          faultImages: doneImages.length ? doneImages : null,
          assignType: this.faultForm.assignType,
          assignUserIds: this.faultForm.assignUserIds.join(',') || null,
          assignTeamId: this.faultForm.assignTeamId || null
        };
        faultService.saveFault(payload).then(() => {
          this.$message.success('故障单创建成功');
          this.$emit('success');
        }).finally(() => {
          this.submitting = false;
        });
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.device-select-tip {
  margin-top: 12px;
  color: #e6a23c;
  font-size: 12px;
}
.device-select-table ::v-deep .el-table__body tr {
  cursor: pointer;

  &:hover > td {
    background-color: #ecf5ff;
  }
}

.step-prev-button:not(.is-disabled) {
  color: #409eff;
  border-color: #409eff;
  background: transparent;
}

.create-steps { margin-bottom: 28px; }
.source-selection { padding: 24px 36px 40px; }
.source-title { font-size: 20px; font-weight: 600; color: #303133; text-align: center; }
.source-description { margin: 12px 0 28px; color: #606266; text-align: center; }
.source-choices { display: flex; gap: 24px; }
.source-choice {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 28px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  background: #fff;
  color: #606266;
  text-align: left;
  cursor: pointer;
  font: inherit;
  &:hover, &:focus { border-color: #409eff; background: #ecf5ff; }
}
.source-choice-title { font-size: 18px; font-weight: 600; color: #303133; }
.source-choice-action { color: #409eff; }
.level-tip { color: #909399; font-size: 12px; }

.step-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #ebeef5;
}

.device-search-bar {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.selected-device-card {
  display: flex;
  align-items: center;
  background: #f5f7fa;
  padding: 10px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  gap: 8px;

  .label { color: #606266; font-size: 13px; }
  .device-name { font-weight: 600; }
  .device-area { color: #999; font-size: 12px; margin-left: 8px; }
}

.pagination-wrap {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}
</style>
